import { ChangeEvent, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useIntl } from "react-intl";

import useImportEquipmentCsv from "../../hooks/useImportEquipmentCsv";
import { useLocalizedPath } from "../../hooks/useLocale";
import {
  EQUIPMENT_CSV_COLUMNS,
  CsvColumnRequirement,
  downloadEquipmentCsvTemplate
} from "../../utils/equipmentCsvTemplate";

import Alert from "../../components/alert/Alert";
import PageSectionHeader from "../../components/PageSectionHeader";
import DownloadIcon from "../../components/icons/Download";
import UploadIcon from "../../components/icons/Upload";
import EquipmentIcon from "../../components/icons/Equipment";

const REQUIREMENT_STYLES: Record<CsvColumnRequirement, string> = {
  required: "bg-[#FFE6E6] text-[#B42318]",
  recommended: "bg-orange-100 text-orange-700",
  optional: "bg-gray-100 text-gray-600"
};

const RULE_KEYS = ["structure", "separator", "idEquipement", "site", "update", "examples", "limits"] as const;
const STEP_KEYS = ["download", "fill", "import"] as const;

const ImportTemplate = () => {
  const intl = useIntl();
  const localizedPath = useLocalizedPath();
  const csvInput = useRef<HTMLInputElement>(null);
  const {
    loading: isImportLoading,
    error: importError,
    response: importResponse,
    upload: uploadCsv
  } = useImportEquipmentCsv();

  const onSelectCsv = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    uploadCsv(file);
    event.target.value = "";
  };

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage(
            { id: "equipment.importTemplate.documentTitle" },
            { appName: import.meta.env.VITE_APP_NAME }
          )}
        </title>
      </Helmet>

      <div className="h-full flex flex-col">
        <PageSectionHeader className="mb-8">
          <h2 className="text-2xl font-semibold" style={{ color: "#023865" }}>
            {intl.formatMessage({ id: "equipment.importTemplate.heading" })}
          </h2>
          <p className="mt-2 text-sm text-gray-600 max-w-3xl">
            {intl.formatMessage({ id: "equipment.importTemplate.subtitle" })}
          </p>
        </PageSectionHeader>

        <section className="bg-white rounded-2xl p-6 mb-6" style={{ border: "1px solid #E4E7EC" }}>
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <EquipmentIcon className="w-6 h-6 text-gray-700 mb-2" />
              <h3 className="text-lg font-semibold text-gray-900">
                {intl.formatMessage({ id: "equipment.importTemplate.downloadTitle" })}
              </h3>
              <p className="mt-1 text-sm text-gray-600 max-w-2xl">
                {intl.formatMessage({ id: "equipment.importTemplate.downloadDescription" })}
              </p>
            </div>
            <div className="flex flex-col items-stretch lg:items-end gap-3 shrink-0">
              <button
                type="button"
                className="px-4 py-3 flex items-center justify-center gap-2 text-white rounded-lg w-full lg:w-72"
                style={{ backgroundColor: "#E94E1B" }}
                onClick={downloadEquipmentCsvTemplate}
              >
                <DownloadIcon className="w-5 h-5" />
                {intl.formatMessage({ id: "equipment.importTemplate.downloadButton" })}
              </button>
              <button
                type="button"
                className="px-4 py-3 flex items-center justify-center gap-2 text-white rounded-lg disabled:opacity-70 disabled:cursor-not-allowed w-full lg:w-72"
                style={{ backgroundColor: "#023865" }}
                onClick={() => csvInput.current?.click()}
                disabled={isImportLoading}
              >
                <UploadIcon className="w-5 h-5" />
                {isImportLoading
                  ? intl.formatMessage({ id: "equipment.list.importing" })
                  : intl.formatMessage({ id: "equipment.list.importCsv" })}
              </button>
              <input ref={csvInput} type="file" accept=".csv" className="hidden" onChange={onSelectCsv} />
              <Link
                className="px-4 py-3 flex items-center justify-center gap-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 w-full lg:w-72"
                to={localizedPath("/dashboard/admin/objects")}
              >
                {intl.formatMessage({ id: "equipment.importTemplate.goToList" })}
              </Link>
            </div>
          </div>

          {importError && <Alert className="my-5 w-fit" type="error" message={importError.message} />}
          {importResponse && (
            <Alert
              className="my-5 w-fit"
              type="success"
              message={intl.formatMessage(
                { id: "equipment.list.importSummary" },
                {
                  message: importResponse.message,
                  imported: importResponse.imported,
                  updated: importResponse.updated,
                  skipped: importResponse.skipped
                }
              )}
            />
          )}

          <ol className="mt-6 grid gap-4 md:grid-cols-3">
            {STEP_KEYS.map((step, index) => (
              <li key={step} className="rounded-xl bg-gray-50 p-4" style={{ border: "1px solid #E4E7EC" }}>
                <span
                  className="inline-flex w-7 h-7 items-center justify-center rounded-full text-sm font-semibold text-white"
                  style={{ backgroundColor: "#0066B1" }}
                >
                  {index + 1}
                </span>
                <h4 className="mt-3 text-sm font-semibold text-gray-900">
                  {intl.formatMessage({ id: `equipment.importTemplate.steps.${step}.title` })}
                </h4>
                <p className="mt-1 text-sm text-gray-600">
                  {intl.formatMessage({ id: `equipment.importTemplate.steps.${step}.description` })}
                </p>
              </li>
            ))}
          </ol>
        </section>

        <section className="bg-white rounded-2xl p-6 mb-6" style={{ border: "1px solid #E4E7EC" }}>
          <h3 className="text-lg font-semibold text-gray-900">
            {intl.formatMessage({ id: "equipment.importTemplate.rulesTitle" })}
          </h3>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-gray-600">
            {RULE_KEYS.map((rule) => (
              <li key={rule}>{intl.formatMessage({ id: `equipment.importTemplate.rules.${rule}` })}</li>
            ))}
          </ul>
        </section>

        <section className="bg-white rounded-2xl p-6" style={{ border: "1px solid #E4E7EC" }}>
          <h3 className="text-lg font-semibold text-gray-900">
            {intl.formatMessage({ id: "equipment.importTemplate.fieldsTitle" })}
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            {intl.formatMessage({ id: "equipment.importTemplate.fieldsSubtitle" })}
          </p>

          <div className="mt-5 overflow-x-auto">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500">
                  <th className="px-3 py-2 font-medium">{intl.formatMessage({ id: "equipment.importTemplate.colPosition" })}</th>
                  <th className="px-3 py-2 font-medium">{intl.formatMessage({ id: "equipment.importTemplate.colHeader" })}</th>
                  <th className="px-3 py-2 font-medium">{intl.formatMessage({ id: "equipment.importTemplate.colRequirement" })}</th>
                  <th className="px-3 py-2 font-medium">{intl.formatMessage({ id: "equipment.importTemplate.colDescription" })}</th>
                  <th className="px-3 py-2 font-medium">{intl.formatMessage({ id: "equipment.importTemplate.colExample" })}</th>
                </tr>
              </thead>
              <tbody>
                {EQUIPMENT_CSV_COLUMNS.map((column) => (
                  <tr key={column.header} className="border-b border-gray-100 align-top">
                    <td className="px-3 py-3 text-gray-500">{column.index + 1}</td>
                    <td className="px-3 py-3 font-mono text-xs font-semibold text-gray-900">{column.header}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${REQUIREMENT_STYLES[column.requirement]}`}>
                        {intl.formatMessage({ id: `equipment.importTemplate.requirement.${column.requirement}` })}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-gray-600">
                      {intl.formatMessage({ id: `equipment.importTemplate.fields.${column.key}.description` })}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs text-gray-500 break-all">{column.example || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  );
};

export default ImportTemplate;
